'use client';
import React, { useEffect, useState } from 'react';
import { AuthEventKeys, AuthEvents, AuthUser, LoginScreen, useAuthStore } from '@/auth';
import { createEventBus } from '@/shared/utils';
import { BackgroundWavesScreen } from '@/shared/ui';
import { Auth as AuthDict } from '@/shared/i18n';
import { useProfile, useProfileStore } from '@/profile';
import { Profile as ProfileDict } from '@/shared/i18n';
import { useRouter } from 'next/navigation';
import { useFirebaseAuth } from '@/shared/hooks';
import { checkPaymentStatus } from '../../../modules/profile/services';
import { CheckoutAfterLogin } from 'src/modules/auth/types/checkout-after-login';
import { createSubscriptionCheckoutSession } from 'src/services/checkout-session';

interface AuthContainerProps {
  dict: AuthDict; // Definisce la prop dict come di tipo Auth
  dictProfile: ProfileDict;
  register?: boolean;
  completeRegistration?: boolean;
}

// Import the redirect configuration
const REDIRECT_CONFIG = {
  redirectPaths: {
    paymentUpdate: '/aggiorna-pagamento',
    onboardingStart: '/profilo/onboarding/start',
    home: '/my-home',
  },
};

function AuthContainer({
  dict,
  dictProfile,
  register = false,
  completeRegistration = false,
}: AuthContainerProps) {
  const authEventBus = createEventBus<AuthEvents>('auth-scope');
  const [viewReady, setViewReady] = useState(false); // Stato per controllare il rendering
  const { setUser } = useAuthStore();
  const { error } = useProfileStore();
  const firebaseAuth = useFirebaseAuth();

  const { refreshProfile } = useProfile(firebaseAuth, dictProfile);

  const router = useRouter();

  const handleViewEvent = () => {
    setViewReady(true);
  };

  const profileNotFound = error?.message?.includes('404');

  const handleLoginSuccess = async ({
    user,
    checkoutAfterLoginObj,
  }: {
    user: AuthUser;
    checkoutAfterLoginObj?: CheckoutAfterLogin;
  }) => {
    setUser(user);

    try {
      const needsToUpdatePayment = await checkPaymentStatus(user.uid);

      if (needsToUpdatePayment) {
        router.push(REDIRECT_CONFIG.redirectPaths.paymentUpdate);
        return;
      }
    } catch (err) {
      // Error handling silently fails
    }

    if (checkoutAfterLoginObj && checkoutAfterLoginObj.priceId) {
      createSubscriptionCheckoutSession({
        userUid: user?.uid || '',
        successUrl: `${window.location.origin}/order-confirmation`,
        cancelUrl: `${window.location.origin}/my-home`,
        priceId: checkoutAfterLoginObj.priceId,
        trialPeriodDays: checkoutAfterLoginObj.trialPeriodDays,
        couponName: checkoutAfterLoginObj.couponName,
      });
      return;
    }

    const error = await refreshProfile({
      email: user?.email,
      onSuccessCallback: (profile) => {
        if (!profile?.email) {
          router.push(REDIRECT_CONFIG.redirectPaths.onboardingStart);
        } else {
          router.push(REDIRECT_CONFIG.redirectPaths.home);
        }
      },
    });

    if (error?.message?.includes('404')) {
      router.push(REDIRECT_CONFIG.redirectPaths.onboardingStart);
    }
  };

  useEffect(() => {
    authEventBus.on(AuthEventKeys.login.success, handleLoginSuccess);

    return () => {
      authEventBus.off(AuthEventKeys.login.success, handleLoginSuccess);
    };
  }, [authEventBus]);

  useEffect(() => {
    const eventKey =
      register && !completeRegistration ? AuthEventKeys.register.view : AuthEventKeys.login.view;

    if (completeRegistration) {
      authEventBus.emit(AuthEventKeys.completeRegistration.view, undefined);
      authEventBus.on(AuthEventKeys.completeRegistration.view, handleViewEvent);
    } else {
      authEventBus.emit(eventKey, undefined);
      authEventBus.on(eventKey, handleViewEvent);
    }

    return () => {
      if (completeRegistration) {
        authEventBus.off(AuthEventKeys.completeRegistration.view, handleViewEvent);
      } else authEventBus.off(eventKey, handleViewEvent);
    };
  }, [authEventBus, register, completeRegistration]);

  if (!viewReady) {
    return <div className="flex items-center justify-center w-full h-full bg-black" />;
  }

  // Se la pagina non è ancora pronta, mostriamo un blocco placeholder nero
  if (!firebaseAuth || !viewReady) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black">
        {/* Qui puoi mettere un'animazione di caricamento, se lo desideri */}
      </div>
    );
  }
  return (
    <>
      <div className="flex z-10 flex-col self-center max-w-[600px] w-full gap-4">
        <LoginScreen
          eventBus={authEventBus}
          dict={dict}
          auth={firebaseAuth}
          loginProfileError={profileNotFound ? null : error}
        />
      </div>
      <BackgroundWavesScreen eventBus={authEventBus} />
    </>
  );
}

export default AuthContainer;
